import { create } from 'zustand';
import type { KdsTicket, KdsServerMessage } from '../types';

export type ConnectionState = 'connected' | 'reconnecting' | 'disconnected';

/** Ticket Map key: "${order_id}_${batch_seq}_${destination_id}" */
function ticketKey(orderId: string, batchSeq: number, destinationId: string): string {
  return `${orderId}_${batchSeq}_${destinationId}`;
}

interface KdsStoreState {
  tickets: Map<string, KdsTicket>;
  connectionState: ConnectionState;
  lastUpdate: number;
  /** Incremented on each Snapshot — allows hooks to detect full state replacements */
  snapshotVersion: number;
}

interface KdsStoreActions {
  applyMessage: (msg: KdsServerMessage) => void;
  setConnectionState: (state: ConnectionState) => void;
  reset: () => void;
}

export const useKdsStore = create<KdsStoreState & KdsStoreActions>((set) => ({
  tickets: new Map(),
  connectionState: 'disconnected',
  lastUpdate: 0,
  snapshotVersion: 0,

  applyMessage: (msg) =>
    set((state) => {
      const map = new Map(state.tickets);
      switch (msg.type) {
        case 'Snapshot':
          return {
            tickets: new Map(
              msg.tickets.map((t) => [ticketKey(t.order_id, t.batch_seq, t.destination_id), t]),
            ),
            lastUpdate: Date.now(),
            snapshotVersion: state.snapshotVersion + 1,
          };
        case 'TicketUpdated': {
          const key = ticketKey(msg.ticket.order_id, msg.ticket.batch_seq, msg.ticket.destination_id);
          map.set(key, msg.ticket);
          return { tickets: map, lastUpdate: Date.now() };
        }
        case 'TicketRemoved': {
          const key = ticketKey(msg.order_id, msg.batch_seq, msg.destination_id);
          map.delete(key);
          return { tickets: map, lastUpdate: Date.now() };
        }
        case 'HistoryPage':
        case 'Ping':
          return state;
      }
    }),

  setConnectionState: (connectionState) => set({ connectionState }),
  reset: () => set({ tickets: new Map(), connectionState: 'disconnected', lastUpdate: 0, snapshotVersion: 0 }),
}));
